import React, { useState } from "react";
import { ChevronDown, Plus, Pencil, Copy, Trash2 } from "lucide-react";
import { CATEGORY_MAP, APPROVAL_META, fmtNumber, fmtDate } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function MaterialSection({ category, items = [], onAdd, onEdit, onDuplicate, onDelete, readOnly, defaultOpen }) {
  const cat = CATEGORY_MAP[category];
  const [open, setOpen] = useState(defaultOpen ?? true);

  return (
    <section
      data-testid={`material-section-${category}`}
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <button
        data-testid={`material-section-toggle-${category}`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-700">{cat.title}</h3>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-600">
            {items.length}
          </span>
        </div>
        <ChevronDown className={cn("h-5 w-5 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {items.length > 0 && (
            <div className="overflow-x-auto mf-scrollbar">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 text-sm uppercase tracking-wide text-slate-500">
                    <th className="px-6 py-3 font-semibold">Item</th>
                    <th className="px-4 py-3 font-semibold">Supplier</th>
                    {category === "threads" && <th className="px-4 py-3 font-semibold">Code Number</th>}
                    <th className="px-4 py-3 font-semibold">Consumption</th>
                    <th className="px-4 py-3 font-semibold">Required qty</th>
                    <th className="px-4 py-3 font-semibold">Approval</th>
                    <th className="px-4 py-3 font-semibold">Book by</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const ap = APPROVAL_META[it.approval_status] || APPROVAL_META.pending;
                    const overdue =
                      it.approval_status === "pending" &&
                      it.to_be_booked_on &&
                      new Date(it.to_be_booked_on) < new Date();
                    return (
                      <tr
                        key={it.id}
                        data-testid={`material-row-${it.id}`}
                        className="border-t border-slate-100 text-base text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <td className="px-6 py-3 font-semibold text-slate-800">{it.name || "—"}</td>
                        <td className="px-4 py-3">{it.supplier || "—"}</td>
                        {category === "threads" && <td className="px-4 py-3">{it.art_number || "—"}</td>}
                        <td className="px-4 py-3">
                          {fmtNumber(it.consumption)} {it.consumption_unit}
                        </td>
                        <td className="px-4 py-3">{fmtNumber(it.required_qty)}</td>
                        <td className="px-4 py-3">
                          <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-sm font-semibold", ap.badge)}>
                            {ap.label}
                            {overdue && " · Overdue"}
                          </span>
                        </td>
                        <td className={cn("px-4 py-3", overdue && "font-semibold text-rose-600")}>
                          {fmtDate(it.to_be_booked_on)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`material-edit-${it.id}`}
                              onClick={() => onEdit(it)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200"
                              title={readOnly ? "View" : "Edit"}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            {!readOnly && (
                              <>
                                <button
                                  data-testid={`material-duplicate-${it.id}`}
                                  onClick={() => onDuplicate(it)}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200"
                                  title="Duplicate"
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                                <button
                                  data-testid={`material-delete-${it.id}`}
                                  onClick={() => onDelete(it)}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {items.length === 0 && (
            <p className="px-6 py-6 text-base text-slate-400">No {cat.singular} items added yet.</p>
          )}

          {!readOnly && (
            <div className="px-6 py-4">
              <button
                data-testid={`add-${category}-btn`}
                onClick={onAdd}
                className="inline-flex h-11 items-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50 px-5 text-base font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100"
              >
                <Plus className="h-5 w-5" /> {cat.addLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
